import { create } from "zustand";
import { dbAll, dbDelete, dbPut } from "../storage/db.js";
import { activeProfileId, useProfiles } from "../settings/profileStore.js";
import { requireFeature } from "./proFeatures.js";

/**
 * Favorites and user-hidden content (Section 11 steps 2–3), scoped to the
 * active local profile. The first profile keeps the historic "local" id, so
 * existing rows remain owned by it without a destructive data migration.
 */

/** What can be favorited. EPG programmes are deliberately absent — a programme
 * is transient, so favoriting one means favoriting its channel. */
export type FavoriteKind = "channel" | "movie" | "series";

/** What a user can hide. `category` hides every channel under it at once. */
export type HiddenKind = "channel" | "category";

/**
 * Sentinel "category" id for the ★ Favorites filter in the Movies/Series/Live
 * toolbars. Real provider category ids are opaque slugs; a leading
 * double-underscore cannot collide with them.
 */
export const FAVORITES_CATEGORY = "__favorites__";

type RecordKind = FavoriteKind | "hidden-channel" | "hidden-category";

export interface FavoriteRecord {
  id: string;
  profileId: string;
  contentType: RecordKind;
  contentRef: string;
  addedAt: string;
  /** Who hid this channel: the user, or the Section 12 health system. Only
   * system-hidden channels are ever auto-unhidden by recovery checks — a
   * user-hidden channel must never silently reappear. Absent = user (legacy). */
  hiddenBy?: "user" | "system";
}

function recordKey(profileId: string, contentType: RecordKind, contentRef: string): string {
  return `${profileId}:${contentType}:${contentRef}`;
}

function hiddenType(kind: HiddenKind): RecordKind {
  return kind === "channel" ? "hidden-channel" : "hidden-category";
}

const mapKey = (contentType: RecordKind, ref: string) => `${contentType}:${ref}`;
let hydrationGeneration = 0;
interface HydrationChange {
  contentType: RecordKind;
  record: FavoriteRecord | null;
}
interface HydrationRun {
  profileId: string;
  generation: number;
  changes: Map<string, HydrationChange>;
  promise: Promise<void>;
}
let hydrationPromise: HydrationRun | null = null;

/** Apply an optimistic mutation after the current IndexedDB snapshot is read. */
function noteHydrationChange(
  profileId: string,
  contentType: RecordKind,
  contentRef: string,
  record: FavoriteRecord | null,
): void {
  const run = hydrationPromise;
  if (run?.profileId !== profileId) return;
  run.changes.set(mapKey(contentType, contentRef), { contentType, record });
}

interface FavoritesState {
  /** Keys are `${contentType}:${contentRef}`; each row records its profile. */
  favorites: Record<string, FavoriteRecord>;
  hidden: Record<string, FavoriteRecord>;
  /** False until the active profile's IndexedDB read completes. */
  hydrated: boolean;
  /** Clear and reload the cached view for the newly active profile. */
  resetForProfileSwitch: () => void;

  hydrate: () => Promise<void>;
  isFavorite: (kind: FavoriteKind, ref: string) => boolean;
  toggleFavorite: (kind: FavoriteKind, ref: string) => void;
  favoritesOf: (kind: FavoriteKind) => string[];

  isHidden: (kind: HiddenKind, ref: string) => boolean;
  toggleHidden: (kind: HiddenKind, ref: string) => void;
  hiddenOf: (kind: HiddenKind) => string[];
  /** Whether a channel is hidden by the health system (not the user). */
  isSystemHidden: (ref: string) => boolean;
  /** Health-system hide: never overwrites a user hide. */
  systemHideChannel: (ref: string) => void;
  /** Recovery removes only system hides — a user-hidden channel stays hidden. */
  systemUnhideChannel: (ref: string) => void;
  /** Clear every hidden entry for the active profile. */
  unhideAll: () => Promise<void>;
}

export const useFavoritesStore = create<FavoritesState>()((set, get) => ({
  favorites: {},
  hidden: {},
  hydrated: false,

  resetForProfileSwitch: () => {
    // Invalidate a read already in flight so it cannot repaint the old profile.
    hydrationGeneration += 1;
    hydrationPromise = null;
    set({ favorites: {}, hidden: {}, hydrated: false });
    // Reload immediately so pages waiting on `hydrated` don't remain blank
    // after a profile switch. The generation guard prevents the old read from
    // repainting this newly selected profile.
    void get().hydrate();
  },

  hydrate: async () => {
    if (get().hydrated) return;
    const profileId = activeProfileId();
    if (hydrationPromise?.profileId === profileId) return hydrationPromise.promise;
    const generation = ++hydrationGeneration;
    const active: HydrationRun = { profileId, generation, changes: new Map(), promise: Promise.resolve() };
    const promise = (async () => {
      const rows = await dbAll<FavoriteRecord>("favorites").catch(() => [] as FavoriteRecord[]);
      if (generation !== hydrationGeneration || profileId !== activeProfileId()) return;
      // The generation check above is the last await boundary. Snapshot the
      // change journal and release the in-flight slot before set() can notify a
      // subscriber that synchronously issues another optimistic mutation.
      const changes = [...active.changes];
      if (hydrationPromise === active) hydrationPromise = null;

      const favorites: Record<string, FavoriteRecord> = {};
      const hidden: Record<string, FavoriteRecord> = {};
      for (const row of rows) {
        if (!row?.contentType || !row.contentRef || row.profileId !== profileId) continue;
        const key = mapKey(row.contentType, row.contentRef);
        if (row.contentType === "hidden-channel" || row.contentType === "hidden-category") {
          hidden[key] = row;
        } else {
          favorites[key] = row;
        }
      }
      // Keep optimistic additions/updates made while IndexedDB was loading.
      for (const row of Object.values(get().favorites)) {
        if (row.profileId === profileId) favorites[mapKey(row.contentType, row.contentRef)] = row;
      }
      for (const row of Object.values(get().hidden)) {
        if (row.profileId === profileId) hidden[mapKey(row.contentType, row.contentRef)] = row;
      }
      // An optimistic deletion has no row to overlay. Apply explicit changes
      // last, or an older IndexedDB snapshot can resurrect a just-removed item.
      for (const [key, change] of changes) {
        const destination = change.contentType === "hidden-channel" || change.contentType === "hidden-category"
          ? hidden
          : favorites;
        if (change.record) destination[key] = change.record;
        else delete destination[key];
      }
      set({ favorites, hidden, hydrated: true });
    })();
    active.promise = promise;
    hydrationPromise = active;
    try {
      await promise;
    } finally {
      if (hydrationPromise === active) hydrationPromise = null;
    }
  },

  isFavorite: (kind, ref) => get().favorites[mapKey(kind, ref)]?.profileId === activeProfileId(),

  toggleFavorite: (kind, ref) => {
    const profileId = activeProfileId();
    if (!get().hydrated) {
      void get().hydrate().then(() => {
        if (profileId === activeProfileId()) get().toggleFavorite(kind, ref);
      });
      return;
    }
    const key = mapKey(kind, ref);
    const id = recordKey(profileId, kind, ref);
    const stored = get().favorites[key];
    const existing = stored?.profileId === profileId ? stored : undefined;
    if (existing) {
      const next = { ...get().favorites };
      delete next[key];
      noteHydrationChange(profileId, kind, ref, null);
      set({ favorites: next });
      void dbDelete("favorites", id);
      return;
    }
    // Favourites are Pro (ADR 2026-10-10). What was saved stays and can be removed above; adding
    // another asks.
    if (!requireFeature("favorites")) return;
    const record: FavoriteRecord = {
      id,
      profileId,
      contentType: kind,
      contentRef: ref,
      addedAt: new Date().toISOString(),
    };
    noteHydrationChange(profileId, kind, ref, record);
    set({ favorites: { ...get().favorites, [key]: record } });
    void dbPut("favorites", record, record.id);
  },

  favoritesOf: (kind) =>
    Object.values(get().favorites)
      .filter((row) => row.profileId === activeProfileId() && row.contentType === kind)
      .sort((a, b) => b.addedAt.localeCompare(a.addedAt))
      .map((row) => row.contentRef),

  isHidden: (kind, ref) => get().hidden[mapKey(hiddenType(kind), ref)]?.profileId === activeProfileId(),

  toggleHidden: (kind, ref) => {
    const profileId = activeProfileId();
    if (!get().hydrated) {
      void get().hydrate().then(() => {
        if (profileId === activeProfileId()) get().toggleHidden(kind, ref);
      });
      return;
    }
    const type = hiddenType(kind);
    const key = mapKey(type, ref);
    const id = recordKey(profileId, type, ref);
    const stored = get().hidden[key];
    const existing = stored?.profileId === profileId ? stored : undefined;
    if (existing) {
      const next = { ...get().hidden };
      delete next[key];
      noteHydrationChange(profileId, type, ref, null);
      set({ hidden: next });
      void dbDelete("favorites", id);
      return;
    }
    const record: FavoriteRecord = {
      id,
      profileId,
      contentType: type,
      contentRef: ref,
      addedAt: new Date().toISOString(),
      hiddenBy: "user",
    };
    noteHydrationChange(profileId, type, ref, record);
    set({ hidden: { ...get().hidden, [key]: record } });
    void dbPut("favorites", record, record.id);
  },

  hiddenOf: (kind) =>
    Object.values(get().hidden)
      .filter((row) => row.profileId === activeProfileId() && row.contentType === hiddenType(kind))
      .map((row) => row.contentRef),

  isSystemHidden: (ref) => {
    const row = get().hidden[mapKey("hidden-channel", ref)];
    return row?.profileId === activeProfileId() && row.hiddenBy === "system";
  },

  systemHideChannel: (ref) => {
    const profileId = activeProfileId();
    if (!get().hydrated) {
      void get().hydrate().then(() => {
        if (profileId === activeProfileId()) get().systemHideChannel(ref);
      });
      return;
    }
    const key = mapKey("hidden-channel", ref);
    const existing = get().hidden[key];
    if (existing?.profileId === profileId && existing.hiddenBy !== "system") return;
    const id = recordKey(profileId, "hidden-channel", ref);
    const record: FavoriteRecord = {
      id,
      profileId,
      contentType: "hidden-channel",
      contentRef: ref,
      addedAt: new Date().toISOString(),
      hiddenBy: "system",
    };
    noteHydrationChange(profileId, "hidden-channel", ref, record);
    set({ hidden: { ...get().hidden, [key]: record } });
    void dbPut("favorites", record, record.id);
  },

  systemUnhideChannel: (ref) => {
    const profileId = activeProfileId();
    if (!get().hydrated) {
      void get().hydrate().then(() => {
        if (profileId === activeProfileId()) get().systemUnhideChannel(ref);
      });
      return;
    }
    const key = mapKey("hidden-channel", ref);
    const existing = get().hidden[key];
    if (!existing || existing.profileId !== profileId || existing.hiddenBy !== "system") return;
    const next = { ...get().hidden };
    delete next[key];
    set({ hidden: next });
    noteHydrationChange(profileId, "hidden-channel", ref, null);
    void dbDelete("favorites", existing.id);
  },

  unhideAll: async () => {
    const profileId = activeProfileId();
    await get().hydrate();
    if (profileId !== activeProfileId()) return;
    const rows = Object.values(get().hidden).filter((row) => row.profileId === profileId);
    const hidden = Object.fromEntries(Object.entries(get().hidden).filter(([, row]) => row.profileId !== profileId));
    set({ hidden });
    for (const row of rows) noteHydrationChange(profileId, row.contentType, row.contentRef, null);
    await Promise.all(rows.map((row) => dbDelete("favorites", row.id).catch(() => undefined)));
  },
}));

// Change the view as soon as the active profile changes. This matters because
// the Home route stays mounted in <Activity> and can otherwise retain cached data.
useProfiles.subscribe((state, previous) => {
  if (state.activeProfileId !== previous.activeProfileId) {
    useFavoritesStore.getState().resetForProfileSwitch();
  }
});

/** Hydrate at module load; these stars are needed on Home and the library pages. */
void useFavoritesStore.getState().hydrate();
